<template>
  <div class="container-fluid">
    <div class="card mb-25 border-0 rounded-0 bg-white shadow-sm">
      <!-- Step Progress Tracker -->
      <StepProgressTracker :current-step="'final'" :besc-id="bescId" />

      <!-- Header bleu -->
      <div class="card-header d-flex justify-content-between align-items-center py-4 px-4"
        style="background-color: #0a59a4;">
        <div>
          <h3 class="text-white fw-bold mb-1">
            Étape 4/4 – Récapitulatif et Soumission – {{ besc.refbesc || 'Chargement...' }}
          </h3>
          <small class="text-white opacity-75">
            Référence : {{ besc.refbesc }} . Créé le {{ formatDate(besc.createdAt) }}
          </small>
        </div>
      </div>
      <div class="card-body p-15 p-sm-20 p-md-30 letter-spacing">
        <!-- ====================== INFORMATIONS GÉNÉRALES ====================== -->
        <div class="mb-4">
          <h5 class="text-primary fw-bold border-bottom pb-2">Informations générales</h5>
        </div>
        <div class="row g-4 mb-5">
          <div class="col-md-4">
            <label class="text-black fw-semibold">Numéro de connaissement</label>
            <p class="mb-0">{{ besc.numeroConnaissement || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Pays d'origine</label>
            <p class="mb-0">{{ besc.paysOrigine?.name_fr || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Incoterm</label>
            <p class="mb-0">{{ besc.incoterm?.code || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Type de paiement fret</label>
            <p class="mb-0">{{ besc.typeFret?.libelle || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Destination finale</label>
            <p class="mb-0">{{ besc.destinationFinalePays?.name_fr || '-' }}</p>
          </div>
        </div>

        <!-- ====================== PARTIES PRENANTES ====================== -->
        <div class="mb-4">
          <h5 class="text-primary fw-bold border-bottom pb-2">Parties prenantes</h5>
        </div>
        <div class="row g-4 mb-5">
          <div class="col-md-3" v-if="importateur">
            <label class="text-black fw-semibold">Expéditeur</label>
            <p class="mb-0">{{ importateur.acteur.nomComplet || '-' }}</p>
          </div>
          <div class="col-md-3" v-if="importateur">
            <label class="text-black fw-semibold">Importateur</label>
            <p class="mb-0">{{ importateur.acteur.nomComplet || '-' }}</p>
          </div>
          <div class="col-md-3" v-if="transitaire">
            <label class="text-black fw-semibold">Transitaire</label>
            <p class="mb-0">{{ transitaire.acteur.nomComplet || '-' }}</p>
          </div>
          <div class="col-md-3" v-if="partieANotifier">
            <label class="text-black fw-semibold">Partie à notifier</label>
            <p class="mb-0">{{ partieANotifier.acteur.nomComplet || '-' }}</p>
          </div>
        </div>

        <!-- ====================== TRANSPORT & ITINÉRAIRE ====================== -->
        <div class="mb-4">
          <h5 class="text-primary fw-bold border-bottom pb-2">Transport & itinéraire</h5>
        </div>
        <div class="row g-4 mb-5">
          <div class="col-md-4" v-if="transporteur">
            <label class="text-black fw-semibold">Transporteur</label>
            <p class="mb-0">{{ transporteur.acteur.nomComplet || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Navire</label>
            <p class="mb-0">{{ besc.vessel?.name || '-' }} <small v-if="besc.vessel?.imo" class="text-muted">(IMO: {{
              besc.vessel.imo }})</small></p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Port de chargement</label>
            <p class="mb-0">{{ besc.portChargement?.nomPort || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">Port de déchargement</label>
            <p class="mb-0">{{ besc.portDechargement?.nomPort || '-' }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">ETD (Départ estimé)</label>
            <p class="mb-0">{{ formatDate(besc.etd) }}</p>
          </div>
          <div class="col-md-4">
            <label class="text-black fw-semibold">ETA (Arrivée estimée)</label>
            <p class="mb-0">{{ formatDate(besc.eta) }}</p>
          </div>

          <!-- Transbordement -->
          <div class="col-12" v-if="besc.transbordement === 'oui'">
            <div class="alert alert-info" v-if="transbordement">
              <strong>Transbordement :</strong> Oui<br>
              Navire après transbordement : {{ besc.navireApresTransbordement?.name || '-' }}<br>
              Transporteur après transbordement : {{ transbordement.acteur.nomComplet || '-' || '-' }}
            </div>
          </div>
        </div>
        <div class="mb-4">
          <h5 class="text-primary fw-bold border-bottom pb-2">
            Marchandises ({{ totalMarchandises }})
          </h5>
        </div>

        <div v-if="besc.bls?.length" class="mb-5">
          <div class="accordion" id="blAccordion">
            <div v-for="(bl, blIndex) in besc.bls" :key="blIndex" class="accordion-item border mb-3 shadow-sm">
              <!-- Header de l'accordéon -->
              <h2 class="accordion-header">
                <button class="accordion-button collapsed bg-blue-header text-white fw-bold" type="button"
                  :data-bs-toggle="'collapse'" :data-bs-target="'#bl' + blIndex"
                  :aria-expanded="blIndex === 0 ? 'true' : 'false'" :aria-controls="'bl' + blIndex">
                  <i class="fas fa-file-alt me-3"></i>
                  BL N° {{ bl.blNumber }}
                  <span class="badge bg-white text-primary ms-3">
                    {{ bl.bescLignes?.length || 0 }} marchandise(s)
                  </span>
                  <span v-if="bl.conteneurs?.length" class="badge bg-white text-primary ms-2">
                    {{ bl.conteneurs.length }} conteneur(s)
                  </span>
                </button>
              </h2>

              <div :id="'bl' + blIndex" class="accordion-collapse collapse" :class="{ show: blIndex === 0 }"
                data-bs-parent="#blAccordion">
                <div class="accordion-body p-0">

                  <!-- Table des marchandises -->
                  <div v-if="bl.bescLignes?.length" class="p-3">
                    <div class="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                      <h6 class="text-primary fw-bold mb-0">
                        <i class="fas fa-boxes me-2"></i>Détail des marchandises
                      </h6>
                      <div class="dropdown">
                      </div>
                    </div>
                    <div class="table-responsive">
                      <table class="table table-hover align-middle mb-0">
                        <thead style="background-color: #0a59a4;" class="text-white">
                          <tr>
                            <th>Description</th>
                            <th class="text-center">Qté</th>
                            <th class="text-center">Nb Colis</th>
                            <th class="text-end">Val. comm.</th>
                            <th class="text-end">Val. déclarée</th>
                            <th class="text-end">Poids brut</th>
                            <th class="text-end">Poids net</th>
                            <th class="text-end">Volume</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="ligne in bl.bescLignes" :key="ligne.id">
                            <td>{{ ligne.descriptionMarchandise || '-' }}</td>
                            <td class="text-center">{{ ligne.quantite || '-' }}</td>
                            <td class="text-center">{{ ligne.nombreColis || '-' }}</td>
                            <td class="text-end">{{ formatNumber(ligne.valeurCommerciale) }} {{ ligne.devise || 'USD' }}
                            </td>
                            <td class="text-end">{{ formatNumber(ligne.valeurDeclaree) }} {{ ligne.devise || 'USD' }}
                            </td>
                            <td class="text-end">{{ formatNumber(ligne.poidsBrut) }} kg</td>
                            <td class="text-end">{{ formatNumber(ligne.poidsNet) }} kg</td>
                            <td class="text-end">{{ formatNumber(ligne.volume) }} m³</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <!-- Bouton pour voir les conteneurs -->
                  <div v-if="bl.conteneurs?.length" class="p-3 bg-light border-top text-center">
                    <button @click="showConteneurs(bl.conteneurs, bl.blNumber)" class="btn btn-primary btn-lg">
                      <i class="fas fa-box me-2"></i>
                      Voir les {{ bl.conteneurs.length }} conteneur(s)
                    </button>
                  </div>
                  <!-- Message si vide -->
                  <div v-if="!bl.bescLignes?.length && !bl.conteneurs?.length" class="text-center text-muted py-4">
                    <i class="fas fa-inbox fa-2x mb-2 opacity-50"></i>
                    <p class="mb-0">Aucune marchandise ni conteneur pour ce BL.</p>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center text-muted py-4">
          <i class="fas fa-inbox fa-3x mb-3 opacity-50"></i>
          <p class="mb-0">Aucun BL enregistré.</p>
        </div>
        <div v-if="showConteneursModal" class="modal-backdrop fade show" @click="showConteneursModal = false"></div>

        <div v-if="showConteneursModal" class="modal fade show d-block" tabindex="-1">
          <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content">

              <div class="modal-header" style="background-color: #0a59a4;">
                <h5 class="modal-title text-white">
                  <i class="fas fa-box me-2"></i>
                  Conteneurs du BL N° {{ selectedBlNumber }}
                </h5>
                <button type="button" class="btn-close btn-close-white" @click="showConteneursModal = false"></button>
              </div>

              <div class="modal-body p-4">
                <div class="row g-3">
                  <div v-for="(conteneur, index) in selectedConteneurs" :key="conteneur.id" class="col-md-6">
                    <div class="card border-primary h-100 shadow-sm">
                      <div class="card-header bg-primary text-white">
                        <i class="fas fa-cube me-2"></i>
                        Conteneur {{ index + 1 }}
                      </div>
                      <div class="card-body">
                        <div class="mb-3 pb-3 border-bottom">
                          <label class="text-muted small fw-semibold d-block mb-1">
                            <i class="fas fa-barcode me-1"></i>Numéro de conteneur
                          </label>
                          <p class="mb-0 fw-bold text-primary fs-5">
                            {{ conteneur.numeroConteneur || 'Non renseigné' }}
                          </p>
                        </div>

                        <div class="row g-3">
                          <div class="col-4">
                            <label class="text-muted small fw-semibold d-block mb-1">
                              <i class="fas fa-lock me-1"></i>N° Scellé
                            </label>
                            <p v-if="conteneur.numeroScelle" class="mb-0 text-dark fw-bold">
                              {{ conteneur.numeroScelle }}
                            </p>
                            <p v-else class="mb-0 text-muted small fst-italic">
                              Non renseigné
                            </p>
                          </div>

                          <div class="col-4">
                            <label class="text-muted small fw-semibold d-block mb-1">
                              <i class="fas fa-weight-hanging me-1"></i>Poids brut
                            </label>
                            <p v-if="conteneur.poidsBrut" class="mb-0 text-dark fw-bold">
                              {{ formatNumber(conteneur.poidsBrut) }} kg
                            </p>
                            <p v-else class="mb-0 text-muted small fst-italic">
                              Non renseigné
                            </p>
                          </div>

                          <div class="col-4">
                            <label class="text-muted small fw-semibold d-block mb-1">
                              <i class="fas fa-cube me-1"></i>Volume
                            </label>
                            <p v-if="conteneur.volume" class="mb-0 text-dark fw-bold">
                              {{ formatNumber(conteneur.volume) }} m³
                            </p>
                            <p v-else class="mb-0 text-muted small fst-italic">
                              Non renseigné
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="modal-footer bg-light">
                <button type="button" class="btn btn-secondary" @click="showConteneursModal = false">
                  <i class="fas fa-times me-2"></i>
                  Fermer
                </button>
              </div>

            </div>
          </div>
        </div>
        <div class="mb-4">
          <h5 class="text-primary fw-bold border-bottom pb-2">Documents joints ({{ besc.documents?.length || 0 }})</h5>
        </div>
        <div class="row g-3" v-if="besc.documents?.length">
          <div class="col-md-4" v-for="doc in besc.documents" :key="doc.id">
            <div class="border rounded p-3 d-flex align-items-center justify-content-between">
              <div>
                <strong>{{ doc.typeDocument?.libelle || 'Document' }}</strong><br>
                <small class="text-muted">{{ doc.filename }}</small>
              </div>
              <button class="btn btn-sm btn-primary" @click="openDocument(bescId, doc.id)">
                Voir
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="d-flex justify-content-between mt-4 mb-4">
    <div class="d-flex gap-3">
      <router-link to="/liste-besc" class="btn btn-outline-secondary px-4">
        <i class="fa fa-times me-2"></i>
        Annuler
      </router-link>
      <router-link :to="{ name: 'EditAddBescStep3', params: { id: bescId } }" class="btn btn-outline-danger px-4">
        <i class="fa fa-arrow-left me-2"></i>
        Retour à l'étape 3
      </router-link>
    </div>

    <!-- Groupe de boutons à droite -->
    <div class="d-flex gap-3">
      <button type="button" @click="saveDraftOnly" class="btn btn-outline-primary px-4"
        :disabled="submitting || !hasAllDocuments">
        <i v-if="submitting && actionType === 'draft'" class="fa fa-spinner fa-spin me-2"></i>
        <i v-else class="fa fa-save me-2"></i>
        {{ submitting && actionType === 'draft' ? 'Enregistrement...' : 'Enregistrer brouillon' }}
      </button>
      <button v-if="besc.facture?.status !== 'PAID'" type="button" @click="saveDraft" class="btn btn-primary px-4"
        :disabled="submitting || !hasAllDocuments">
        <i v-if="submitting && actionType === 'submit'" class="fa fa-spinner fa-spin me-2"></i>
        <i v-else class="fa fa-paper-plane me-2"></i>
        {{ submitting && actionType === 'submit' ? 'Soumission...' : 'Soumettre le BESC' }}
      </button>
    </div>
  </div>
  <div v-if="showPaymentModal" class="modal-backdrop fade show"></div>
  <div v-if="showPaymentModal" class="modal fade show d-block" tabindex="-1" style="z-index: 1050;">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content overflow-hidden"
        style="border-radius: 20px; box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);">
        <div class="payment-modal-container">
          <div class="payment-header">
            <h2>Finaliser votre BESC</h2>
            <p>Paiement sécurisé et rapide</p>
            <div class="amount-display">
              {{ formatAmount(facture?.montantTotal || 0) }} EUROS
            </div>
          </div>

          <div class="px-4 pb-4">
            <div class="alert alert-info mb-4">
              <h6 class="mb-2"> Facture : {{ facture?.referenceFacture }}</h6>
              <div class="row small">
                <div class="col-md-6">
                  <p class="mb-1"><strong>Montant HT :</strong> {{ formatAmount(facture?.subtotalAmount) }} EUROS</p>
                  <p class="mb-1"><strong>TVA (18%) :</strong> {{ formatAmount(facture?.taxAmount) }} EUROS</p>
                </div>
                <div class="col-md-6">
                  <p class="mb-1"><strong>Échéance :</strong> {{ formatDate(facture?.dueDate) }}</p>
                </div>
              </div>
            </div>

            <div v-if="prepaidAccount" class="alert alert-success small p-3 mb-4">
              <strong>Solde disponible :</strong> {{ formatAmount(prepaidAccount.balance) }} EUROS
            </div>

            <div class="payment-methods-title">
              Choisissez votre moyen de paiement
            </div>

            <div class="payment-methods-grid">
              <div v-if="prepaidAccount && prepaidAccount.balance >= facture?.montantTotal"
                class="payment-method-card recommended" @click="payWithPrepaidAccount" style="cursor: pointer;">
                <div class="payment-icon-wrapper">
                  <i class="fa fa-wallet fa-3x text-primary"></i>
                </div>
                <div class="payment-method-name">Compte Prépayé</div>
                <div class="payment-method-desc">Solde : {{ formatAmount(prepaidAccount.balance) }} EUROS</div>
              </div>

              <div class="payment-method-card recommended" @click="redirectToPaymentMethod('kkiapay')"
                style="cursor: pointer;">
                <div class="payment-icon-wrapper">
                  <img src="/kkiapay.png.webp" alt="KkiaPay" style="width: 60px; height: 60px; object-fit: contain;" />
                </div>
                <div class="payment-method-name">KkiaPay</div>
                <div class="payment-method-desc">MTN • Moov • Carte</div>
              </div>

              <div class="payment-method-card" @click="redirectToPaymentMethod('paypal')" style="cursor: pointer;">
                <div class="payment-icon-wrapper">
                  <img src="/paypal.png.png" alt="PayPal" style="width: 60px; height: 60px; object-fit: contain;" />
                </div>
                <div class="payment-method-name">PayPal</div>
                <div class="payment-method-desc">Compte PayPal</div>
              </div>

              <div class="payment-method-card" @click="redirectToPaymentMethod('stripe')" style="cursor: pointer;">
                <div class="payment-icon-wrapper">
                  <img src="/stripe.png.png" alt="Stripe" style="width: 60px; height: 60px; object-fit: contain;" />
                </div>
                <div class="payment-method-name">Carte Bancaire</div>
                <div class="payment-method-desc">Visa • Mastercard</div>
              </div>
            </div>

            <div v-if="prepaidAccount && prepaidAccount.balance < facture?.montantTotal"
              class="alert alert-warning mt-4">
              <small>
                <i class="fa fa-info-circle me-1"></i>
                Solde insuffisant. Manquant : {{ formatAmount(facture.montantTotal - prepaidAccount.balance) }} EUROS
              </small>
            </div>

            <div class="text-center mt-5">
              <button type="button" class="btn btn-outline-secondary btn-lg me-2" @click="cancelPayment">
                <i class="fa fa-times me-2"></i>
                Annuler et revenir à la liste
              </button>


            </div>

          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- MODAL : Paiement confirmé → UNIQUEMENT IMPRESSIONS DIRECTES -->
  <div v-if="showSuccessModal" class="modal-backdrop fade show"></div>
  <div v-if="showSuccessModal" class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header" style="background-color: #28a745;">
          <h5 class="modal-title text-white">
            <i class="fa fa-check-circle me-2"></i>
            Paiement confirmé !
          </h5>
        </div>

        <div class="modal-body text-center p-4">
          <p class="mb-4">Votre paiement a été confirmé avec succès.<br>Imprimez vos documents maintenant :</p>

          <div class="d-flex flex-column gap-3">
            <button type="button" class="btn btn-success btn-lg" @click="printBESC" :disabled="printingBESC">
              <i v-if="printingBESC" class="fa fa-spinner fa-spin me-2"></i>
              <i v-else class="fa fa-print me-2"></i>
              {{ printingBESC ? 'Préparation de l\'impression...' : 'Imprimer le BESC' }}
            </button>
            <button type="button" class="btn btn-info btn-lg text-white" @click="printFacture"
              :disabled="printingFacture">
              <i v-if="printingFacture" class="fa fa-spinner fa-spin me-2"></i>
              <i v-else class="fa fa-file-invoice-dollar me-2"></i>
              {{ printingFacture ? 'Préparation de l\'impression...' : 'Imprimer la facture' }}
            </button>

            <button type="button" class="btn btn-outline-primary btn-lg" @click="goToList">
              <i class="fa fa-list me-2"></i>
              Voir la liste des BESC
            </button>
          </div>

        </div>
      </div>
    </div>
  </div>

</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Form, Field, ErrorMessage } from 'vee-validate';
import ApiService from '@/services/ApiService'
import { error, monSwal, success } from '@/utils/utils'
import Multiselect from '@vueform/multiselect'
import * as Yup from 'yup';
import JwtService from '@/services/JwtService';
import { onActivated, watch } from 'vue'
import { bescWizard, loadBescProgression, resetBescWizard, updateStepProgression } from '@/utils/bescWizard';
import router from '@/router';
import Swal from 'sweetalert2';
import StepProgressTracker from '@/components/BESC/StepProgressTracker.vue'

export default defineComponent({
  name: 'RecapitulatifV',
  components: {
    StepProgressTracker
  },

  setup() {

    const route = useRoute()
    const router = useRouter()
    const bescId = route.params.id as string
    const getBescId = () => route.params.id as string
    const isAdmin = computed(() => {
      const user = JwtService.getUser() as any;
      return user?.roles?.includes('Administrateur') ?? false;
    });
    const isAgent = computed(() => {
      const user = JwtService.getUser() as any;
      return user?.roles?.includes('Agent') ?? false;
    });
    const besc = ref<any>({})
    const statusHistory = ref<any[]>([])
    const loading = ref(true)
    const bescActeurs = ref<any[]>([])
    const autoFlags = ref<any[]>([])
    const showFlagsModal = ref(false)

    const showConteneursModal = ref(false)
    const selectedConteneurs = ref<any[]>([])
    const selectedBlNumber = ref('')

    const showCommentModal = ref(false)
    const selectedBl = ref<any>(null)
    const selectedBlIndex = ref<number>(-1)

    const currentComment = ref('')
    const validationtaskOptions = ref([])
    const showCorrectionRequestModal = ref(false)
    const selectedValidationTask = ref<string | null>(null);

    const openCorrectionRequestModal = (taskId: string) => {
      selectedValidationTask.value = taskId;
      showCorrectionRequestModal.value = true;
    }
    const addCorrectionRequest = async (values) => {
      if (!validationTasks.value.length) {
        return error("Impossible de récupérer la tâche de validation liée à ce BESC.")
      }

      const payload = {
        besc_file_id: getBescId(),
        validation_task_id: selectedValidationTask.value,
        // validation_task_id: validationTasks.value[0].id,
        requested_at: values.requested_at,
        due_date: values.due_date,
        fields_concerned: values.fields_concerned,
        message_to_requester: values.message_to_requester,
        response_message: null
      }

      await ApiService.post(`/correction`, payload)
      success('Demande enregistrée avec succès !')
      showCorrectionRequestModal.value = false
    }
    const uploadDocuments = async () => {
      const formData = new FormData()
      pieces.forEach((p) => {
        if (p.type_id) formData.append('type_ids[]', p.type_id.toString())
        if (p.fichier) formData.append('files', p.fichier)
      })
      await ApiService.post(`/besc/${bescId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    }
    const typeDocuments = ref<{ value: number; label: string; obligatoire: boolean }[]>([])
    const documentManquant = computed(() => {
      const required = typeDocuments.value.filter(t => t.obligatoire).map(t => t.value)
      const fourni = pieces.map(p => p.type_id)
      return required.filter(r => !fourni.includes(r))
    })
    const pieces = bescWizard.step3.pieces
    // const hasAllDocuments = computed(() => {
    //   if (pieces.length === 0) return false
    //   const allFilled = pieces.every(p => p.type_id && p.fichier)
    //   if (!allFilled) return false
    //   return documentManquant.value.length === 0
    // })

    const hasAllDocuments = computed(() => {
      if (!besc.value.documents || besc.value.documents.length === 0) return false
      const requiredTypes = typeDocuments.value
        .filter(t => t.obligatoire)
        .map(t => t.value)
      const uploadedTypes = besc.value.documents.map(
        (d: any) => d.typeDocument?.id
      )
      return requiredTypes.every(rt => uploadedTypes.includes(rt))
    })

    const canAddPiece = computed(() => {
      if (pieces.length === 0) return true
      const lastPiece = pieces[pieces.length - 1]
      return !!(lastPiece.type_id && lastPiece.fichier)
    })
    console.log('pieces:', pieces)
    console.log('documentManquant:', documentManquant.value)
    console.log('hasAllDocuments:', hasAllDocuments.value)

    const submitting = ref(false)
    const actionType = ref<'draft' | 'submit' | null>(null)

    const saveDraftOnly = async () => {
      if (!hasAllDocuments.value) {
        error('Veuillez ajouter et compléter tous les documents requis')
        return
      }

      actionType.value = 'draft'
      submitting.value = true

      try {
        await ApiService.post(`/besc/${bescId}/create-brouillon`, {})

        success('Brouillon enregistré avec succès !')

        // Rediriger vers la liste des BESC
        resetBescWizard()
        router.push({ name: 'ListeBesc' })

      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors de l\'enregistrement du brouillon')
      } finally {
        submitting.value = false
        actionType.value = null
      }
    }

    const saveDraft = async () => {
      if (!hasAllDocuments.value) {
        error('Veuillez ajouter et compléter tous les documents requis')
        return
      }

      actionType.value = 'submit'
      submitting.value = true

      try {
        const response = await ApiService.post(`/besc/${bescId}/calculate-fees`, {})

        facture.value = response.data.data.facture

        await loadPrepaidAccount()


        // Update final step progression
        updateStepProgression('final', true)

        showPaymentModal.value = true

      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors de la soumission du BESC')
      } finally {
        submitting.value = false
        actionType.value = null
      }
    }

    const loadPrepaidAccount = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}')
        if (!user.acteurId) return

        const res = await ApiService.get(`/prepaid-accounts/acteur/${user.acteurId}`)
        if (res.data.data) {
          const accounts = Array.isArray(res.data.data) ? res.data.data : [res.data.data]
          const activeAccount = accounts.find((acc: any) => acc.status === 'ACTIVE' && Number(acc.balance) > 0)
          if (activeAccount) prepaidAccount.value = activeAccount
        }
      } catch (err: any) {
        if (err.response?.status !== 404) console.error('Erreur compte prépayé:', err)
      }
    }

    const paying = ref(false)
    const showPaymentModal = ref(false)
    const showSuccessModal = ref(false)
    const formatAmount = (amount: number) => {
      return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)
    }

    const facture = ref<any>(null)
    const prepaidAccount = ref<any>(null)


    const payWithPrepaidAccount = async () => {
      if (!prepaidAccount.value || prepaidAccount.value.balance < facture.value.montantTotal) {
        error('Solde insuffisant ou compte indisponible');
        return;
      }

      paying.value = true;
      try {
        console.log(' Débit compte prépayé pour facture:', facture.value.id);

        const response = await ApiService.post(`/payments/prepaid`, {
          factureId: facture.value.id,
          acteurId: prepaidAccount.value.acteurId,
          amount: facture.value.montantTotal,
          paymentMethodType: 'PREPAID_ACCOUNT',
          comment: 'Paiement via compte prépayé'
        });

        console.log(' Réponse paiement:', response.data);

        await new Promise(resolve => setTimeout(resolve, 1000));

        success(' Paiement effectué avec succès !');

        showPaymentModal.value = false;
        showSuccessModal.value = true;

        // Recharger le solde
        await loadPrepaidAccount();

      } catch (err: any) {
        console.error(' Erreur paiement prépayé:', err);
        error(err.response?.data?.message || 'Erreur lors du paiement');
      } finally {
        paying.value = false;
      }
    };

    const redirectToPaymentMethod = async (method: 'kkiapay' | 'stripe' | 'paypal') => {
      try {
        // Stocker les infos en session
        sessionStorage.setItem('pendingPaymentBescId', bescId);
        sessionStorage.setItem('pendingPaymentFactureId', facture.value.id);

        if (method === 'paypal') {
          console.log(' [PAYPAL] Création de la commande PayPal pour BESC:', bescId);

          const { data } = await ApiService.post(
            `/besc/${bescId}/payment/paypal/create-order`,
            { comment: '' }
          );

          console.log(' [PAYPAL] Commande créée:', data.data);

          window.location.href = data.data.approvalUrl;
          return;
        }

        if (method === 'stripe') {
          sessionStorage.setItem('pendingPaymentBescId', bescId);
          router.push(`/paiement-stripe/${bescId}`);
          return;
        }

        if (method === 'kkiapay') {
          sessionStorage.setItem('pendingPaymentBescId', bescId);
          router.push(`/paiement-kkiapay/${bescId}`);
          return;
        }

      } catch (err: any) {
        console.error(' Erreur lancement paiement:', err);
        error(err.response?.data?.message || 'Impossible de lancer le paiement');
      }
    }

    const cancelPayment = () => {
      showPaymentModal.value = false
      router.push({ name: 'ListeBesc' })
    }
    // États pour les impressions
    const printingBESC = ref(false)
    const printingFacture = ref(false)


    // IMPRESSION DIRECTE DE LA FACTURE
    const printFacture = async () => {
      printingFacture.value = true
      try {
        // À adapter si ton endpoint est différent (ex: /besc/{id}/invoice-pdf)
        const response = await ApiService.getFile(`/besc/${bescId}/download-invoice`)
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)

        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        iframe.src = url
        document.body.appendChild(iframe)

        iframe.onload = () => {
          iframe.contentWindow?.focus()
          iframe.contentWindow?.print()
        }

        window.addEventListener('focus', () => {
          setTimeout(() => {
            if (document.body.contains(iframe)) document.body.removeChild(iframe)
            window.URL.revokeObjectURL(url)
          }, 10000)
        }, { once: true })
      } catch (err) {
        error('Erreur lors de l\'impression de la facture')
      } finally {
        printingFacture.value = false
      }
    }

    const comments = ref<any[]>([])
    const showCommentFormModal = ref(false)
    const commentForm = ref<{
      visibility: string
      comment_text: string
      related_entity_type: string
      related_entity_id: string | null
      validation_task_id: string | null
    }>({
      visibility: 'INTERNAL',
      comment_text: '',
      related_entity_type: 'BESC_FILE',
      related_entity_id: null,
      validation_task_id: null
    })

    const editingCommentId = ref<string | null>(null)
    const validationTasks = ref<any[]>([])

    const loadingChecks = ref(false)

    // const selectedValidationTask = ref(null);

    const loadValidationChecks = async (taskId: string) => {
      try {
        loadingChecks.value = true
        const res = await ApiService.get(`/validation-tasks/${taskId}/checks`)
        console.log("niveauuuuuuuuuuuu", res);
        return res.data.data || []
      } catch (err: any) {
        console.error('Erreur chargement checks:', err)
        return []
      } finally {
        loadingChecks.value = false
      }
    }

    // const updateCheck = async (taskId: string, checkId: string, result: string, comments = '') => {
    //   try {
    //     const payload = {
    //       result,
    //       comments,
    //       checked_at: new Date().toISOString()
    //     }

    //     await ApiService.put(`/validation-tasks/${taskId}/checks/${checkId}`, payload)
    //     success('Contrôle mis à jour')

    //     const taskIndex = validationTasks.value.findIndex(t => t.id === taskId)
    //     if (taskIndex !== -1 && validationTasks.value[taskIndex].checks) {
    //       const updatedChecks = await loadValidationChecks(taskId)
    //       validationTasks.value[taskIndex].checks = updatedChecks
    //     }
    //   } catch (err: any) {
    //     error('Erreur lors de la mise à jour du contrôle')
    //   }
    // }


    const openDocument = async (bescId: string, documentId: string) => {
      try {
        if (!bescId || !documentId) {
          console.error("Paramètre manquant", { bescId, documentId });
          return;
        }
        const token = localStorage.getItem("id_token");
        if (!token) {
          console.error("Token manquant !");
          return;
        }

        // chemin relatif à baseURL défini dans ApiService
        const url = `/besc/${bescId}/documents/${documentId}/view`;
        console.log("URL appelée =", url);

        const response = await ApiService.vueInstance.axios.get(url, {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log("Réponse reçue du backend :", response);

        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        console.log("Blob créé :", blob);

        const fileURL = URL.createObjectURL(blob);
        console.log("URL du fichier créé :", fileURL);

        window.open(fileURL, "_blank");
        console.log("Fichier ouvert dans un nouvel onglet ✅");

      } catch (error) {
        console.error("Erreur lors de l'ouverture du document :", error);
      }
    };



    const updateCheck = async (taskId: string, checkId: string, result: string, comments = '') => {
      try {
        const payload = {
          result,
          comments,
          checked_at: new Date().toISOString()
        }

        await ApiService.put(`/validation-tasks/${taskId}/checks/${checkId}`, payload)
        success('Contrôle mis à jour')

        const task = validationTasks.value.find(t => t.id === taskId)
        if (task && task.checks) {
          const check = task.checks.find(c => c.id === checkId)
          if (check) {
            check.result = result
            check.comments = comments
            check.checked_at = payload.checked_at
          }
        }
      } catch (err: any) {
        error('Erreur lors de la mise à jour du contrôle')
      }
    }


    const loadValidationTasks = async () => {
      try {
        const res = await ApiService.get(`/besc/${bescId}/validation-tasks`)
        console.log("infooooooooooooo", res),
          validationTasks.value = res.data.data || []

        for (const task of validationTasks.value) {
          task.checks = await loadValidationChecks(task.id)
        }
      } catch (err: any) {
        console.error('Erreur lors du chargement des tâches de validation')
      }
    }



    const visibiliteOptions = [
      { value: 'INTERNAL', label: 'Interne (visible uniquement par les agents)' },
      { value: 'EXTERNAL', label: 'Externe (visible par le demandeur)' }
    ]

    const resultatCheckOptions = [
      { value: 'PENDING', label: 'En attente' },
      { value: 'PASSED', label: 'Conforme' },
      { value: 'WARNING', label: 'Avertissement' },
      { value: 'FAILED', label: 'Non conforme' },
      { value: 'NOT_APPLICABLE', label: 'Non applicable' }
    ]

    const taskOptions = computed(() => {
      const options = [
        { value: null, label: 'Aucune tâche (commentaire général)' }
      ]

      validationTasks.value.forEach(task => {
        options.push({
          value: task.id,
          label: `${task.task_name} - ${translateStatus(task.status)}${task.nom ? ` (Assigné à : ${task.user.nom} ${task.user.prenom})` : ''}`
        })
      })

      return options
    })

    const getCheckResultClass = (result: string) => {
      const classes: any = {
        'PASSED': 'bg-success',
        'FAILED': 'bg-danger',
        'WARNING': 'bg-warning text-dark',
        'PENDING': 'bg-secondary',
        'NOT_APPLICABLE': 'bg-info'
      }
      return classes[result] || 'bg-secondary'
    }

    const translateStatus = (status: string) => {
      const map: { [key: string]: string } = {
        PENDING: 'En attente',
        PASSED: 'Conforme',
        FAILED: 'Non conforme',
        WARNING: 'Avertissement',
        NOT_APPLICABLE: 'Non applicable',
        COMPLETED: 'Terminé',
        IN_PROGRESS: 'En cours'
      }
      return map[status] || status
    }


    const translateSeverity = (severity: string) => {
      const map: { [key: string]: string } = {
        BLOCKING: 'Bloquant',
        HIGH_RISK: 'Risque élevé',
        WARNING: 'Avertissement',
        INFO: 'Information'
      }
      return map[severity] || severity
    }
    const getCheckResultIcon = (result: string) => {
      const icons: any = {
        'PASSED': 'fas fa-check-circle',
        'FAILED': 'fas fa-times-circle',
        'WARNING': 'fas fa-exclamation-triangle',
        'PENDING': 'fas fa-clock',
        'NOT_APPLICABLE': 'fas fa-minus-circle'
      }
      return icons[result] || 'fas fa-question-circle'
    }

    const getSeverityBadgeClass = (severity: string) => {
      const classes: any = {
        'BLOCKING': 'bg-danger',
        'WARNING': 'bg-warning text-dark',
        'INFO': 'bg-info'
      }
      return classes[severity] || 'bg-secondary'
    }

    const getCheckStats = (checks: any[]) => {
      if (!checks || !checks.length) return null

      return {
        total: checks.length,
        passed: checks.filter(c => c.result === 'PASSED').length,
        failed: checks.filter(c => c.result === 'FAILED').length,
        warning: checks.filter(c => c.result === 'WARNING').length,
        pending: checks.filter(c => c.result === 'PENDING').length,
        completion: Math.round(
          (checks.filter(c => c.result !== 'PENDING').length / checks.length) * 100
        )
      }
    }

    const loadComments = async () => {
      try {
        const res = await ApiService.get(`/besc/${bescId}/comments`)
        console.log("commmennnt", res)
        comments.value = res.data.data || []

      } catch (err: any) {
        error('Erreur lors du chargement des commentaires')
      }
    }

    const statusBadgeClass = computed(() => {
      const code = besc.value.currentStatus?.code
      if (!code) return 'bg-secondary'

      if (code === 'SUBMITTED') {
        // Vérifier si une facture existe et son état de paiement
        const activeFacture = besc.value.factures?.find(
          f => f.status !== 'CANCELLED' && f.status !== 'VOID'
        )

        if (!activeFacture) {
          return 'bg-info text-white'
        }

        if (activeFacture.status === 'PAID') {
          return 'bg-warning text-dark'
        }
        return 'bg-info text-white'
      }

      const map: any = {
        DRAFT: 'bg-secondary text-white',
        PENDING: 'bg-warning text-dark',
        VALIDATED: 'bg-success text-white',
        REJECTED: 'bg-danger text-white',
        CANCELLED: 'bg-dark text-white'
      }

      return map[code] || 'bg-secondary'
    })

    const statusLabel = computed(() => {
      const code = besc.value.currentStatus?.code

      if (code === 'SUBMITTED') {
        const activeFacture = besc.value.factures?.find(
          f => f.status !== 'CANCELLED' && f.status !== 'VOID'
        )

        if (activeFacture?.status === 'PAID') {
          return 'Soumis - En attente de validation'
        }

        return 'Soumis - En attente de paiement'
      }

      return besc.value.currentStatus?.label || 'Inconnu'
    })

    const formatDate = (date: string) => {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('fr-FR')
    }


    const formatDateTime = (date: string) => {
      if (!date) return '-'
      return new Date(date).toLocaleString('fr-FR')
    }

    const formatNumber = (val: any) => {
      if (val === null || val === undefined) return '-'
      return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(val)
    }

    const getDocumentUrl = (docId: string) => {
      return `${process.env.VITE_API_URL}/besc/${bescId}/documents/${docId}/download`
    }

    const openFlags = computed(() =>
      autoFlags.value.filter(f => f.status === 'OPEN')
    )

    const criticalFlags = computed(() =>
      openFlags.value.filter(f => f.is_blocking === true)
    )

    const blockingFlags = computed(() =>
      criticalFlags.value.filter(f => f.severity === 'BLOCKING')
    )

    const highRiskFlags = computed(() =>
      criticalFlags.value.filter(f => f.severity === 'HIGH_RISK')
    )

    const warningFlags = computed(() =>
      openFlags.value.filter(f => f.severity === 'WARNING')
    )

    const infoFlags = computed(() =>
      openFlags.value.filter(f => f.severity === 'INFO')
    )

    const goToList = () => {
      showSuccessModal.value = false
      resetBescWizard()
      router.push({ name: 'ListeBesc' })
    }

    const totalCriticalFlags = computed(() => criticalFlags.value.length)
    const totalBlockingFlags = computed(() => blockingFlags.value.length)
    const totalHighRiskFlags = computed(() => highRiskFlags.value.length)
    const totalWarningFlags = computed(() => warningFlags.value.length)
    const totalInfoFlags = computed(() => infoFlags.value.length)
    const totalAllFlags = computed(() => openFlags.value.length)

    const showConteneurs = (conteneurs: any[], blNumber: string) => {
      selectedConteneurs.value = conteneurs
      selectedBlNumber.value = blNumber
      showConteneursModal.value = true
    }

    const openCommentModal = (bl: any, blIndex: number) => {
      selectedBl.value = bl
      selectedBlIndex.value = blIndex

      commentForm.value = {
        visibility: 'INTERNAL',
        comment_text: '',
        related_entity_type: 'BILL_OF_LADING',
        related_entity_id: bl.id,
        validation_task_id: null
      }

      showCommentFormModal.value = true
    }

    const openCommentModalWithTask = (
      entityType: string,
      entityId: string | null,
      taskId: string | null = null
    ) => {
      commentForm.value = {
        visibility: 'INTERNAL',
        comment_text: '',
        related_entity_type: entityType,
        related_entity_id: entityId,
        validation_task_id: taskId
      }
      editingCommentId.value = null
      showCommentFormModal.value = true
    }

    // const currentUser = ref({ role: 'Administrateur' });
    // const getTaskComments = (taskId: string) => {
    //   let taskComments = comments.value.filter(c => c.validation_task_id === taskId);
    //   const userRole = currentUser.value?.role || 'Transitaire';
    //   if (userRole === 'Transitaire') {
    //     taskComments = taskComments.filter(c => c.visibility === 'EXTERNAL');
    //   }
    //   return taskComments;
    // }

    const printBESC = async () => {
      printingBESC.value = true
      try {
        const response = await ApiService.getFile(`/besc/${bescId}/download-draft`)
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)

        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        iframe.src = url
        document.body.appendChild(iframe)

        iframe.onload = () => {
          iframe.contentWindow?.focus()
          iframe.contentWindow?.print()
        }

        window.addEventListener('focus', () => {
          setTimeout(() => {
            if (document.body.contains(iframe)) document.body.removeChild(iframe)
            window.URL.revokeObjectURL(url)
          }, 10000)
        }, { once: true })
      } catch (err) {
        error('Erreur lors de l\'impression du BESC')
      } finally {
        printingBESC.value = false
      }
    }


    const getTaskComments = (taskId: string) => {
      console.log('🔍 getTaskComments appelée pour taskId:', taskId);
      console.log('👤 isAdmin ?', isAdmin.value);
      console.log('👤 isAgent ?', isAgent.value);
      console.log('📊 Nombre total de commentaires pour cette tâche :',
        comments.value.filter(c => c.validation_task_id === taskId).length
      );
      console.log('🔒 Parmi eux, combien sont INTERNAL :',
        comments.value.filter(c => c.validation_task_id === taskId && c.visibility === 'INTERNAL').length
      );

      const allCommentsForTask = comments.value.filter(
        c => c.validation_task_id === taskId
      );

      if (isAdmin.value || isAgent.value) {
        console.log('✅ Utilisateur autorisé → on affiche TOUT');
        return allCommentsForTask;
      }

      console.log('❌ Utilisateur non autorisé → on filtre seulement EXTERNAL');
      return allCommentsForTask.filter(c => c.visibility === 'EXTERNAL');
    };
    const getTaskEntityComments = (taskId: string, entityType: string, entityId: string) =>
      comments.value.filter(c =>
        c.validation_task_id === taskId &&
        c.related_entity_type === entityType &&
        c.related_entity_id === entityId
      )

    const openGlobalCommentModal = () => {
      commentForm.value = {
        visibility: 'INTERNAL',
        comment_text: '',
        related_entity_type: 'BESC_FILE',
        related_entity_id: null,
        validation_task_id: null
      }
      editingCommentId.value = null
      showCommentFormModal.value = true
    }

    const openDocumentCommentModal = (documentId: string) => {
      commentForm.value = {
        visibility: 'INTERNAL',
        comment_text: '',
        related_entity_type: 'SUPPORTING_DOCUMENT',
        related_entity_id: documentId,
        validation_task_id: null
      }
      editingCommentId.value = null
      showCommentFormModal.value = true
    }

    const editComment = (comment: any) => {
      commentForm.value = {
        visibility: comment.visibility,
        comment_text: comment.commentText,
        related_entity_type: comment.relatedEntityType,
        related_entity_id: comment.relatedEntityId,
        validation_task_id: comment.validationTaskId
      }
      editingCommentId.value = comment.id
      showCommentFormModal.value = true
    }

    const closeCommentModal = () => {
      showCommentModal.value = false
      selectedBl.value = null
      selectedBlIndex.value = -1
      currentComment.value = ''
    }

    const saveComment = async () => {
      try {
        if (!commentForm.value.comment_text.trim()) {
          error('Le commentaire ne peut pas être vide')
          return
        }

        const payload = {
          visibility: commentForm.value.visibility,
          commentText: commentForm.value.comment_text,
          relatedEntityType: commentForm.value.related_entity_type,
          relatedEntityId: commentForm.value.related_entity_id,
          validationTaskId: commentForm.value.validation_task_id
        }

        if (editingCommentId.value) {
          await ApiService.put(`/besc/${bescId}/comments/${editingCommentId.value}`, payload)
          success('Commentaire modifié avec succès !')
        } else {
          await ApiService.post(`/besc/${bescId}/comments`, payload)
          success('Commentaire ajouté avec succès !')
        }

        await loadComments()
        closeCommentFormModal()
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors de l\'enregistrement du commentaire')
      }
    }
    const deleteComment = async (commentId: string) => {
      const confirmed = await monSwal(
        'Êtes-vous sûr de vouloir supprimer ce commentaire ?',
        'Supprimer',
        'btn btn-danger',
        'Annuler',
        'Suppression annulée'
      );

      if (!confirmed) return;

      try {
        await ApiService.delete(`/besc/${bescId}/comments/${commentId}`);
        await loadComments();
        success('Commentaire supprimé avec succès !');
      } catch (err: any) {
        error('Erreur lors de la suppression du commentaire');
      }
    };
    const loadTypes = async () => {
      loading.value = true
      try {
        const res = await ApiService.get('/typeDocumentBescs')
        typeDocuments.value = res.data.data.data.map((x: any) => ({
          value: x.id,
          label: x.libelle,
          obligatoire: x.obligatoire
        }))
      } catch (err) {
        error('Erreur lors du chargement des types de documents')
      } finally {
        loading.value = false
      }
    }

    const closeCommentFormModal = () => {
      showCommentFormModal.value = false
      editingCommentId.value = null
      commentForm.value = {
        visibility: 'INTERNAL',
        comment_text: '',
        related_entity_type: 'BESC_FILE',
        related_entity_id: null,
        validation_task_id: null
      }
    }

    const globalComments = computed(() =>
      comments.value.filter(c => c.relatedEntityType === 'BESC_FILE')
    )

    // const getBlComments = (blId: string) =>
    //   comments.value.filter(c =>
    //     c.relatedEntityType === 'BILL_OF_LADING' && c.relatedEntityId === blId
    //   )

    const getBlComments = (blId: string) => {
      let blComments = comments.value.filter(c =>
        c.relatedEntityType === 'BILL_OF_LADING' && c.relatedEntityId === blId
      );
      if (!(isAdmin.value || isAgent.value)) {
        blComments = blComments.filter(c => c.visibility === 'EXTERNAL');
      }

      return blComments;
    };

    const getDocumentComments = (docId: string) =>
      comments.value.filter(c =>
        c.relatedEntityType === 'SUPPORTING_DOCUMENT' && c.relatedEntityId === docId
      )

    const totalMarchandises = computed(() => {
      if (!besc.value.bls?.length) return 0
      return besc.value.bls.reduce((sum: number, bl: any) => {
        return sum + (bl.bescLignes?.length || 0)
      }, 0)
    })

    onActivated(() => {
      console.log(' Page activée - Rechargement des données')
      loadBesc()
      loadComments()
      loadValidationTasks()
    })

    watch(
      () => route.params.id,
      (newId) => {
        if (newId) {
          console.log(' Changement d\'ID détecté:', newId)
          loadBesc()
        }
      }
    )

    const loadBesc = async () => {
      try {
        loading.value = true
        console.log(' [VUE] Chargement BESC ID:', bescId)

        // Charger le BESC
        const bescRes = await ApiService.get(`/besc/${bescId}`)
        besc.value = bescRes.data.data

        console.log(' [VUE] BESC chargé:', {
          ref: besc.value.refbesc,
          currentStatusId: besc.value.currentStatusId,
          currentStatus: besc.value.currentStatus,
          nombreFactures: besc.value.factures?.length || 0
        })

        // Charger l'historique des statuts
        const historyRes = await ApiService.get(`/besc/${bescId}/status-history`)
        statusHistory.value = historyRes.data.data || []

        // Charger les flags automatiques
        const flagsRes = await ApiService.get(`/besc/${bescId}/auto-flags`)
        autoFlags.value = Array.isArray(flagsRes.data) ? flagsRes.data : flagsRes.data.data || []

        // Charger les acteurs
        bescActeurs.value = besc.value.bescActeurs || []

        console.log(' [VUE] Toutes les données chargées avec succès')
        console.log(' [VUE] Statut actuel:', {
          code: besc.value.currentStatus?.code,
          label: besc.value.currentStatus?.label
        })

      } catch (err: any) {
        console.error(' [VUE] Erreur chargement BESC:', err)
        error(err.response?.data?.message || 'Impossible de charger le BESC')
        router.push('/liste-besc')
      } finally {
        loading.value = false
      }
    }


    const importateur = computed(() =>
      bescActeurs.value.find(a => a.acteur.typeActeur.libelle === "Importateur")
    )

    const transitaire = computed(() =>
      bescActeurs.value.find(a => a.acteur.typeActeur.libelle === "Transitaire")
    )

    const expediteur = computed(() =>
      bescActeurs.value.find(a => a.acteur.typeActeur.libelle === "Expediteur")
    )

    const transporteur = computed(() =>
      bescActeurs.value.find(a => a.acteur.typeActeur.libelle === "Transporteur")
    )

    const partieANotifier = computed(() =>
      bescActeurs.value.find(a => a.acteur.typeActeur.libelle === "Partie à notifier")
    )

    const transbordement = computed(() =>
      bescActeurs.value.find(a => a.acteur.typeActeur.libelle === "Transporteur Après transbordement")
    )

    const printPDF = async () => {
      try {
        const response = await ApiService.getFile(`/besc/${bescId}/download-draft`)
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
      } catch (err) {
        error('Erreur lors du téléchargement du PDF')
      }
    }

    const goToEdit = () => {
      router.push({ name: 'AddBescStep1', params: { id: bescId } })
    }


    onMounted(() => {

      loadBesc()
      loadComments()
      loadValidationTasks()
    })


    onMounted(async () => {
      await loadTypes();

      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');           // PayPal
      const txId = urlParams.get('transaction_id');   // KKiaPay
      const paymentIntent = urlParams.get('payment_intent_client_secret'); // Stripe

      if (token) {
        // === RETOUR PAYPAL ===
        console.log('✅ [PAYPAL] Retour de PayPal détecté - Token:', token);

        Swal.fire({
          title: '⏳ Finalisation du paiement PayPal...',
          html: 'Veuillez patienter pendant la capture du paiement.',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        try {
          // Capture du paiement PayPal
          const response = await ApiService.post(
            `/besc/${bescId}/payment/paypal/capture`,
            { orderID: token }
          );

          console.log('✅ [PAYPAL] Capture réussie:', response.data);

          Swal.close();

          // Charger les infos actualisées du BESC
          const { data: bescData } = await ApiService.get(`/besc/${bescId}`);
          facture.value = bescData.data.facture;

          // Afficher le modal de succès avec impressions
          showPaymentModal.value = false;
          showSuccessModal.value = true;

          // Nettoyer l'URL
          window.history.replaceState({}, '', window.location.pathname);

        } catch (err: any) {
          console.error('❌ [PAYPAL] Erreur capture:', err);
          Swal.fire({
            icon: 'error',
            title: 'Erreur de paiement',
            text: err.response?.data?.message || 'La capture du paiement PayPal a échoué',
            confirmButtonText: 'Retour'
          }).then(() => {
            router.push({ name: 'ListeBesc' });
          });
        }
      }
      else if (txId) {
        // === RETOUR KKIAPAY ===
        console.log('✅ [KKIAPAY] Retour de KKiaPay détecté - Transaction ID:', txId);

        Swal.fire({
          title: '⏳ Vérification du paiement KKiaPay...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        try {
          const response = await ApiService.post(
            `/besc/${bescId}/payment/kkiapay/verify`,
            { transactionId: txId }
          );

          console.log('✅ [KKIAPAY] Vérification réussie:', response.data);

          Swal.close();

          const { data: bescData } = await ApiService.get(`/besc/${bescId}`);
          facture.value = bescData.data.facture;

          showPaymentModal.value = false;
          showSuccessModal.value = true;

          window.history.replaceState({}, '', window.location.pathname);

        } catch (err: any) {
          console.error('❌ [KKIAPAY] Erreur vérification:', err);
          Swal.fire({
            icon: 'error',
            title: 'Erreur de paiement',
            text: err.response?.data?.message || 'La vérification du paiement KKiaPay a échoué',
            confirmButtonText: 'Retour'
          }).then(() => {
            router.push({ name: 'ListeBesc' });
          });
        }
      }
    });

    return {
      openDocument,
      printingBESC,
      besc,
      printingFacture,
      showPaymentModal,
      isAgent,
      formatAmount,
      printFacture,
      facture,
      bescId,
      //   validationCorrectionSchema,
      statusHistory,
      loading,
      statusBadgeClass,
      formatDate,
      formatDateTime,
      formatNumber,
      getDocumentUrl,
      printPDF,
      goToEdit,
      hasAllDocuments,
      validationtaskOptions,
      addCorrectionRequest,
      bescActeurs,
      importateur,
      transitaire,
      prepaidAccount,
      expediteur,
      transporteur,
      canAddPiece,
      partieANotifier,
      transbordement,
      redirectToPaymentMethod,
      autoFlags,
      blockingFlags,
      highRiskFlags,
      warningFlags,
      showFlagsModal,
      goToList,
      infoFlags,
      openCorrectionRequestModal,
      totalCriticalFlags,
      totalBlockingFlags,
      totalHighRiskFlags,
      totalWarningFlags,
      totalInfoFlags,
      totalAllFlags,
      submitting,
      totalMarchandises,
      showConteneursModal,
      selectedConteneurs,
      selectedBlNumber,
      showConteneurs,
      saveDraft,
      saveDraftOnly,
      actionType,
      showCommentModal,
      cancelPayment,
      showSuccessModal,
      selectedBl,
      selectedBlIndex,
      currentComment,
      closeCommentModal,
      comments,
      selectedValidationTask,
      showCommentFormModal,
      commentForm,
      editingCommentId,
      openGlobalCommentModal,
      openDocumentCommentModal,
      openCommentModal,
      editComment,
      deleteComment,
      showCorrectionRequestModal,
      closeCommentFormModal,
      isAdmin,
      globalComments,
      getBlComments,
      getDocumentComments,
      payWithPrepaidAccount,
      saveComment,
      validationTasks,
      loadValidationTasks,
      openCommentModalWithTask,
      getTaskComments,
      getTaskEntityComments,
      loadingChecks,
      loadValidationChecks,
      updateCheck,
      getCheckResultClass,
      getCheckResultIcon,
      getSeverityBadgeClass,
      getCheckStats,
      translateStatus,
      translateSeverity,
      statusLabel,
      visibiliteOptions,
      resultatCheckOptions,
      taskOptions,
      printBESC,

    }
  }
})
</script>

<style scoped>
.section-title h5 {
  position: relative;
  padding-left: 15px;
}

.section-title h5::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background-color: #0a59a4;
  border-radius: 2px;
}

.timeline {
  position: relative;
  padding-left: 30px;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 10px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #ddd;
}

.timeline-item {
  position: relative;
  margin-bottom: 20px;
}

.timeline-marker {
  position: absolute;
  left: -30px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #0a59a4;
  border: 3px solid white;
  box-shadow: 0 0 0 3px #0a59a4;
}

.timeline-content {
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  border-left: 4px solid #0a59a4;
}

.bg-blue-header {
  background-color: #0a59a4 !important;
}

.accordion-button:not(.collapsed) {
  background-color: #0a59a4 !important;
  color: white !important;
  box-shadow: none;
}

.accordion-button:focus {
  box-shadow: 0 0 0 0.25rem rgba(10, 89, 164, 0.25);
  border-color: #0a59a4;
}

.accordion-button::after {
  filter: brightness(0) invert(1);
}

.accordion-item {
  border-radius: 8px !important;
  overflow: hidden;
}

.accordion-button {
  font-size: 1.05rem;
  padding: 1rem 1.25rem;
}

.card.border-primary {
  border-width: 2px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.card.border-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(10, 89, 164, 0.15);
}

/* Ajouter à la fin de ta section <style scoped> existante */

/* Styles pour les checks */
.checks-list {
  max-height: 600px;
  overflow-y: auto;
}

.check-item {
  transition: all 0.2s ease;
  border-width: 2px;
}

.check-item:hover {
  transform: translateX(5px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.check-item.border-success {
  background: linear-gradient(to right, #f0fff4 0%, #ffffff 50%);
}

.check-item.border-danger {
  background: linear-gradient(to right, #fff5f5 0%, #ffffff 50%);
}

.check-item.border-warning {
  background: linear-gradient(to right, #fffbeb 0%, #ffffff 50%);
}

.check-item .form-select-sm,
.check-item .form-control-sm {
  font-size: 0.875rem;
}

.progress {
  border-radius: 10px;
  overflow: hidden;
}

.progress-bar {
  transition: width 0.6s ease;
}

/* Animation au chargement */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.check-item {
  animation: slideIn 0.3s ease;
}

.small-multiselect :deep(.multiselect) {
  font-size: 0.875rem;
}

.small-multiselect :deep(.multiselect-wrapper) {
  min-height: 38px;
}

.small-multiselect :deep(.multiselect-single-label) {
  font-size: 0.875rem;
}

.check-item.automatic {
  background: linear-gradient(to right, #e7f3ff 0%, #ffffff 50%);
  border-left: 4px solid #0d6efd !important;
}

.check-item.automatic .badge.bg-info {
  animation: pulse-glow 2s infinite;
}

@keyframes pulse-glow {

  0%,
  100% {
    opacity: 1;
    box-shadow: 0 0 0 rgba(13, 110, 253, 0.4);
  }

  50% {
    opacity: 0.85;
    box-shadow: 0 0 8px rgba(13, 110, 253, 0.6);
  }
}

.check-item.automatic :deep(.multiselect.is-disabled),
.check-item.automatic textarea:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
  opacity: 0.7;
}

.modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1040;
}

/* Styles du modal paiement (inchangés) */
.payment-modal-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.payment-header {
  background: linear-gradient(135deg, #0a59a4 0%, #0d6ec9 100%);
  color: white;
  padding: 2rem;
  border-radius: 16px 16px 0 0;
  margin: -1.5rem -1.5rem 2rem -1.5rem;
  text-align: center;
}

.payment-header h2 {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
}

.payment-header p {
  margin: 0.5rem 0 0 0;
  opacity: 0.95;
  font-size: 0.95rem;
}

.amount-display {
  background: rgba(255, 255, 255, 0.15);
  padding: 1rem;
  border-radius: 12px;
  margin: 1rem 0;
  color: white;
  font-size: 1.8rem;
  font-weight: 700;
}

.payment-methods-title {
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 2rem 0 1.5rem 0;
}

.payment-methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  padding: 0 1rem;
}

.payment-method-card {
  position: relative;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 2rem 1.5rem;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.payment-method-card:hover {
  transform: translateY(-8px);
  border-color: #0a59a4;
  box-shadow: 0 20px 40px rgba(10, 89, 164, 0.15);
}

.payment-method-card.recommended::before {
  content: '⭐ Recommandé';
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 0.35rem 1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.payment-icon-wrapper {
  width: 80px;
  height: 80px;
  margin: 0 auto 1rem;
  background: #f8fafc;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.payment-method-card:hover .payment-icon-wrapper {
  background: #0a59a4;
  transform: scale(1.1);
}

.payment-method-name {
  font-weight: 700;
  font-size: 1.1rem;
  color: #1e293b;
  margin: 0.75rem 0 0.5rem 0;
}

.payment-method-desc {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
}

@media (max-width: 768px) {
  .payment-methods-grid {
    grid-template-columns: 1fr;
    padding: 0;
  }

  .payment-header h2 {
    font-size: 1.5rem;
  }
}
</style>